// Admin Settings — platform-wide knobs (single page, vertical card list).
//
// Source-of-truth admin-standalone.html:1394 surfaces a flatter set of cards:
// Profile, Commission Rate, Appearance, Demo Data. The master doc (and
// inventory note 02_admin_supplier.md) call out a richer set: tier rates,
// stripe configuration, system AI, feature flags, maintenance mode. This
// extraction promotes those into proper card sections.
//
// Backend status:
//   GET  /api/admin/settings          — real (server.js:431; reads JSON file)
//   POST /api/admin/settings          — real (server.js:435; partial update)
//   GET  /api/admin/feature-flags     — real (modules/feature_flags.js:70).
//                                       Returns per-retailer flags + defaults.
//                                       The spec asks for global flags
//                                       (retailer_id IS NULL); the closest the
//                                       backend exposes today is the
//                                       `defaults` block from this endpoint,
//                                       which we surface as the global view.
//   POST /api/admin/feature-flags/:retailerId — real (per-retailer toggle)
//                                       — we don't use it here; global toggle
//                                       writes to /api/admin/settings as a
//                                       feature_flags blob.
//   Stripe rotation / connect status — not exposed today; rendered as stub.
//
// "System AI" toggle is rendered disabled per spec ("Coming soon").
// "Propose change" on tier rates is a stub — the rates live in code
// (modules/stripe.js:39-45) and changing them is governance, not a setting.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Constants ────────────────────────────────────────────────────────────────

// Mirrors modules/stripe.js:39-45 (TIERS). Kept inline as a typed display
// fallback; the real source lives server-side and is read-only here.
const TIER_RATES: ReadonlyArray<{ tier: number; name: string; rate: string; band: string }> = [
  { tier: 1, name: "Tier 1", rate: "3.3%", band: "Up to £250k" },
  { tier: 2, name: "Tier 2", rate: "2.8%", band: "£250k – £500k" },
  { tier: 3, name: "Tier 3", rate: "2.3%", band: "£500k – £750k" },
  { tier: 4, name: "Tier 4", rate: "1.8%", band: "£750k – £1m" },
  { tier: 5, name: "Tier 5", rate: "1.5%", band: "£1m+" },
];

const COMMISSION_CAP = "£500/transaction";

// ── Types ────────────────────────────────────────────────────────────────────

interface AdminSettings {
  default_commission_rate?: number;
  feature_flags?: Record<string, boolean>;
  maintenance_mode?: boolean;
  maintenance_message?: string;
  system_ai_enabled?: boolean;
  /** Global customer-render feature flag. Off by default — when off, no render
   *  jobs fire and the render UI is hidden across wizard/customer/retailer. */
  customerRenderEnabled?: boolean;
  stripe_webhook_rotated_at?: string;
  // unknown fields preserved on round-trip
  [k: string]: unknown;
}

interface SettingsResponse {
  ok?: boolean;
  settings?: AdminSettings;
}

interface FeatureFlagsResponse {
  ok?: boolean;
  defaults?: Record<string, boolean>;
  retailers?: Array<{ id: string; name: string; flags: Record<string, boolean> }>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

function fmtDate(iso: string | undefined): string {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

function humaniseFlag(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

// ── Page ─────────────────────────────────────────────────────────────────────

const settingsPage: Page = {
  name: "settings",
  label: "Platform Settings",
  icon: "⚙️",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();

    // ── State ──────────────────────────────────────────────────────────────
    let settings: AdminSettings | null = null;
    let featureDefaults: Record<string, boolean> = {};
    let loading = true;
    let savingKey: string | null = null;

    // ── DOM scaffold ──────────────────────────────────────────────────────
    const root = document.createElement("section");
    root.className = "aset-page";
    root.innerHTML = `
      <header class="aset-header">
        <div>
          <h1>Platform Settings</h1>
          <p>Platform-wide configuration. Read-only items are derived from code and require deploy to change.</p>
        </div>
      </header>
      <div class="aset-content" data-role="content"></div>
    `;
    container.replaceChildren(root);

    const contentEl = root.querySelector<HTMLDivElement>("[data-role='content']")!;

    // ── Backend calls ─────────────────────────────────────────────────────

    async function fetchSettings(): Promise<void> {
      try {
        const resp = await ctx.api<SettingsResponse>("/admin/settings", {
          method: "GET",
          signal: ac.signal,
        });
        settings = resp.settings ?? {};
      } catch (err) {
        if (isAbortError(err)) return;
        if (isNotFound(err)) {
          settings = {};
        } else {
          settings = {};
          console.warn("[admin/settings] /admin/settings failed:", err);
        }
      }
    }

    async function fetchFeatureFlags(): Promise<void> {
      try {
        const resp = await ctx.api<FeatureFlagsResponse>("/admin/feature-flags", {
          method: "GET",
          signal: ac.signal,
        });
        featureDefaults = resp.defaults ?? {};
      } catch (err) {
        if (isAbortError(err)) return;
        if (isNotFound(err)) {
          // Stub minimal defaults so the toggle UI renders something.
          featureDefaults = {
            quote_builder: true,
            ai_tools: true,
            catalogue_manager: true,
            social_media: true,
            accounting_integration: true,
          };
        } else {
          featureDefaults = {};
          console.warn("[admin/settings] /admin/feature-flags failed:", err);
        }
      }
    }

    async function patchSettings(partial: Partial<AdminSettings>, key: string): Promise<void> {
      if (savingKey) return;
      savingKey = key;
      renderContent();
      try {
        const resp = await ctx.api<SettingsResponse>("/admin/settings", {
          method: "POST",
          signal: ac.signal,
          body: JSON.stringify(partial),
        });
        settings = resp.settings ?? { ...(settings ?? {}), ...partial };
        ctx.showToast("Saved", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        if (isNotFound(err)) {
          // Endpoint not wired: keep local change so the UI feels alive.
          settings = { ...(settings ?? {}), ...partial };
          ctx.showToast("Saved locally — endpoint not wired", "info");
        } else {
          ctx.showToast(err instanceof Error ? err.message : "Save failed", "error");
        }
      } finally {
        savingKey = null;
        renderContent();
      }
    }

    // ── Render ────────────────────────────────────────────────────────────

    function renderContent(): void {
      if (loading) {
        contentEl.innerHTML = `<div class="adm-loading">Loading settings…</div>`;
        return;
      }
      contentEl.innerHTML = [
        renderTierRatesCard(),
        renderCommissionCard(),
        renderStripeCard(),
        renderSystemAiCard(),
        renderCustomerRenderCard(),
        renderFeatureFlagsCard(),
        renderMaintenanceCard(),
      ].join("");
    }

    function renderCustomerRenderCard(): string {
      const on = settings?.customerRenderEnabled === true;
      const saving = savingKey === "customer_render";
      return `
        <article class="aset-card" data-section="customer-render">
          <header class="aset-card__head">
            <div>
              <h2>Customer Render</h2>
              <p>Global on/off for the AI kitchen render feature. When off, no
                 renders are generated on wizard submission and the render UI is
                 hidden across the wizard splash, customer portal and retailer
                 customer record.</p>
            </div>
          </header>
          <label class="aset-switch">
            <input type="checkbox" data-role="customer-render-toggle" ${on ? "checked" : ""} ${saving ? "disabled" : ""} />
            <span class="aset-switch__track" aria-hidden="true"></span>
            <span class="aset-switch__label">Enable customer render feature</span>
          </label>
        </article>
      `;
    }

    function renderTierRatesCard(): string {
      const rows = TIER_RATES.map((t) => `
        <tr>
          <td><strong>${t.tier}</strong></td>
          <td>${esc(t.name)}</td>
          <td>${esc(t.band)}</td>
          <td><span class="adm-pill">${esc(t.rate)}</span></td>
        </tr>
      `).join("");

      return `
        <article class="aset-card" data-section="tier-rates">
          <header class="aset-card__head">
            <div>
              <h2>Tier rates</h2>
              <p>Read-only — defined in code. Cap: <strong>${esc(COMMISSION_CAP)}</strong>. Source: <code>modules/stripe.js:39-45</code>.</p>
            </div>
            <button type="button" class="adm-btn adm-btn-ghost" data-role="propose-tier-change">Propose change</button>
          </header>
          <table class="adm-table aset-table--rates">
            <thead>
              <tr><th>Tier</th><th>Name</th><th>Annual GMV band</th><th>Rate</th></tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </article>
      `;
    }

    function renderCommissionCard(): string {
      const cur = typeof settings?.default_commission_rate === "number"
        ? settings.default_commission_rate
        : 3.3;
      const saving = savingKey === "default_commission_rate";
      return `
        <article class="aset-card" data-section="commission">
          <header class="aset-card__head">
            <div>
              <h2>Default commission rate</h2>
              <p>Applied to brand-new retailers on day one. They climb the tier ladder automatically as their rolling 12-month GMV grows.</p>
            </div>
          </header>
          <div class="aset-inline-form">
            <input class="adm-input aset-input--narrow"
              type="number" min="0" max="100" step="0.1"
              value="${cur}" data-role="commission-rate" />
            <span class="aset-suffix">%</span>
            <button type="button" class="adm-btn" data-role="commission-save" ${saving ? "disabled" : ""}>
              ${saving ? "Saving…" : "Save"}
            </button>
          </div>
        </article>
      `;
    }

    function renderStripeCard(): string {
      const rotated = settings?.stripe_webhook_rotated_at;
      const saving = savingKey === "stripe_webhook_rotated_at";
      return `
        <article class="aset-card" data-section="stripe">
          <header class="aset-card__head">
            <div>
              <h2>Stripe configuration</h2>
              <p>Platform connect status + webhook hygiene.</p>
            </div>
          </header>
          <dl class="aset-kv">
            <dt>Connect status</dt>
            <dd>
              <span class="adm-pill adm-pill-info">stub</span>
              <span class="aset-muted">Detailed status not yet exposed via API.</span>
            </dd>
            <dt>Webhook secret last rotated</dt>
            <dd>
              <strong>${esc(fmtDate(rotated))}</strong>
              <button type="button" class="adm-btn adm-btn-ghost" data-role="rotate-webhook" ${saving ? "disabled" : ""}>
                ${saving ? "Rotating…" : "Rotate webhook"}
              </button>
            </dd>
          </dl>
        </article>
      `;
    }

    function renderSystemAiCard(): string {
      // Disabled toggle per spec.
      return `
        <article class="aset-card aset-card--ghost" data-section="system-ai">
          <header class="aset-card__head">
            <div>
              <h2>System AI</h2>
              <p>Always-on monitor across retailer, supplier and customer portals. Surfaces anomalies and unanswered conversations to the admin dashboard.</p>
            </div>
            <span class="adm-pill adm-pill-info">Coming soon</span>
          </header>
          <label class="aset-switch aset-switch--disabled">
            <input type="checkbox" disabled />
            <span class="aset-switch__track" aria-hidden="true"></span>
            <span class="aset-switch__label">Enable platform-wide AI monitoring</span>
          </label>
        </article>
      `;
    }

    function renderFeatureFlagsCard(): string {
      const current = { ...featureDefaults, ...(settings?.feature_flags ?? {}) };
      const keys = Object.keys(current).sort();
      const saving = savingKey === "feature_flags";
      const stubbed = Object.keys(featureDefaults).length === 0;

      if (keys.length === 0) {
        return `
          <article class="aset-card" data-section="feature-flags">
            <header class="aset-card__head">
              <div>
                <h2>Feature flags</h2>
                <p>Global toggles applied to every retailer.</p>
              </div>
            </header>
            <div class="adm-empty">No feature flags exposed by the backend yet.</div>
          </article>
        `;
      }

      const items = keys.map((k) => {
        const on = !!current[k];
        return `
          <label class="aset-switch" data-flag="${esc(k)}">
            <input type="checkbox" data-role="flag-toggle" data-flag="${esc(k)}" ${on ? "checked" : ""} ${saving ? "disabled" : ""} />
            <span class="aset-switch__track" aria-hidden="true"></span>
            <span class="aset-switch__label">${esc(humaniseFlag(k))}</span>
          </label>
        `;
      }).join("");

      const banner = stubbed
        ? `<div class="aset-banner">Backend defaults unavailable — showing in-memory list.</div>`
        : "";

      return `
        <article class="aset-card" data-section="feature-flags">
          <header class="aset-card__head">
            <div>
              <h2>Feature flags</h2>
              <p>Global defaults. Per-retailer overrides live on the retailer profile. Source: <code>feature_flags</code> where <code>retailer_id IS NULL</code>.</p>
            </div>
          </header>
          ${banner}
          <div class="aset-flags">${items}</div>
        </article>
      `;
    }

    function renderMaintenanceCard(): string {
      const on = !!settings?.maintenance_mode;
      const message = settings?.maintenance_message ?? "";
      const saving = savingKey === "maintenance";
      return `
        <article class="aset-card" data-section="maintenance">
          <header class="aset-card__head">
            <div>
              <h2>Maintenance mode</h2>
              <p>When on, retailer + supplier portals show a banner with the message below.</p>
            </div>
          </header>
          <label class="aset-switch">
            <input type="checkbox" data-role="maintenance-toggle" ${on ? "checked" : ""} ${saving ? "disabled" : ""} />
            <span class="aset-switch__track" aria-hidden="true"></span>
            <span class="aset-switch__label">Enable maintenance mode</span>
          </label>
          <label class="aset-field">
            <span>Banner message</span>
            <textarea class="adm-textarea" rows="3" data-role="maintenance-message" placeholder="We'll be back shortly — scheduled maintenance.">${esc(message)}</textarea>
          </label>
          <div class="aset-actions">
            <button type="button" class="adm-btn" data-role="maintenance-save" ${saving ? "disabled" : ""}>
              ${saving ? "Saving…" : "Save message"}
            </button>
          </div>
        </article>
      `;
    }

    // ── Event handlers ────────────────────────────────────────────────────

    const onContentClick = (e: MouseEvent): void => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>("button");
      if (!btn) return;
      const role = btn.dataset["role"];

      if (role === "propose-tier-change") {
        ctx.showToast(
          "Tier rates are in code (modules/stripe.js). 'Propose change' would open a governance flow — not wired yet.",
          "info",
        );
        return;
      }

      if (role === "commission-save") {
        const input = contentEl.querySelector<HTMLInputElement>("[data-role='commission-rate']");
        const v = Number(input?.value ?? "");
        if (!Number.isFinite(v) || v < 0 || v > 100) {
          ctx.showToast("Rate must be between 0 and 100", "error");
          return;
        }
        void patchSettings({ default_commission_rate: v }, "default_commission_rate");
        return;
      }

      if (role === "rotate-webhook") {
        if (!window.confirm("Rotate the Stripe webhook secret? Existing webhooks will need re-registering.")) return;
        void patchSettings(
          { stripe_webhook_rotated_at: new Date().toISOString() },
          "stripe_webhook_rotated_at",
        );
        return;
      }

      if (role === "maintenance-save") {
        const msgEl = contentEl.querySelector<HTMLTextAreaElement>("[data-role='maintenance-message']");
        void patchSettings(
          { maintenance_message: msgEl?.value ?? "" },
          "maintenance",
        );
        return;
      }
    };

    const onContentChange = (e: Event): void => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      // Flag toggle — write through immediately.
      if (target instanceof HTMLInputElement && target.dataset["role"] === "flag-toggle") {
        const flag = target.dataset["flag"];
        if (!flag) return;
        const checked = target.checked;
        const nextFlags = { ...(settings?.feature_flags ?? {}), [flag]: checked };
        void patchSettings({ feature_flags: nextFlags }, "feature_flags");
        return;
      }

      // Maintenance toggle — write through immediately.
      if (target instanceof HTMLInputElement && target.dataset["role"] === "maintenance-toggle") {
        void patchSettings({ maintenance_mode: target.checked }, "maintenance");
        return;
      }

      // Customer-render global toggle — write through immediately.
      if (target instanceof HTMLInputElement && target.dataset["role"] === "customer-render-toggle") {
        void patchSettings({ customerRenderEnabled: target.checked }, "customer_render");
        return;
      }
    };

    contentEl.addEventListener("click", onContentClick);
    contentEl.addEventListener("change", onContentChange);

    // ── Boot ──────────────────────────────────────────────────────────────
    renderContent();
    void Promise.all([fetchSettings(), fetchFeatureFlags()]).then(() => {
      loading = false;
      renderContent();
    });

    // ── Cleanup ───────────────────────────────────────────────────────────
    return () => {
      ac.abort();
      contentEl.removeEventListener("click", onContentClick);
      contentEl.removeEventListener("change", onContentChange);
    };
  },
};

registerPage(settingsPage);

export default settingsPage;
